import React from 'react'
import PropTypes from 'prop-types'
import './WingsDateInput.css'

/**
 * WingsDateInput Component
 *
 * A styled date input built on the native <input type="date">, styled to match
 * the Wingspans design system. Accepts and returns ISO date strings
 * (YYYY-MM-DD) for simple integration with form state.
 *
 * @component
 * @example
 * // Basic usage
 * <WingsDateInput
 *   label="Target Date"
 *   value={targetDate}
 *   onChange={(iso) => setTargetDate(iso)}
 * />
 *
 * @example
 * // Optional with helper text
 * <WingsDateInput
 *   label="Due Date (optional)"
 *   helpText="Leave blank if no deadline"
 *   value={dueDate}
 *   onChange={(iso) => setDueDate(iso)}
 *   fullWidth
 * />
 */
const WingsDateInput = ({
  label,
  helpText,
  error,
  id,
  value = '',
  onChange,
  size = 'medium',
  fullWidth = false,
  disabled = false,
  className = '',
  ...props
}) => {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined)

  const handleChange = (e) => {
    onChange?.(e.target.value)
  }

  const wrapperClasses = [
    'wings-date-input-wrapper',
    fullWidth && 'wings-date-input-wrapper--full',
    error && 'wings-date-input-wrapper--error',
    className,
  ].filter(Boolean).join(' ')

  const inputClasses = [
    'wings-date-input',
    `wings-date-input--${size}`,
    error && 'wings-date-input--error',
    disabled && 'wings-date-input--disabled',
  ].filter(Boolean).join(' ')

  return (
    <div className={wrapperClasses}>
      {label && (
        <label className="wings-date-input-label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <input
        id={inputId}
        type="date"
        className={inputClasses}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        aria-describedby={helpText || error ? `${inputId}-hint` : undefined}
        aria-invalid={!!error}
        {...props}
      />
      {(helpText || error) && (
        <span
          id={`${inputId}-hint`}
          className={`wings-date-input-hint${error ? ' wings-date-input-hint--error' : ''}`}
        >
          {error || helpText}
        </span>
      )}
    </div>
  )
}

WingsDateInput.propTypes = {
  /** Label displayed above the input */
  label: PropTypes.string,
  /** Helper text displayed below the input */
  helpText: PropTypes.string,
  /** Error message — puts the input in an error state and replaces helpText */
  error: PropTypes.string,
  /** HTML id for the input; auto-generated from label if omitted */
  id: PropTypes.string,
  /** ISO date string value (YYYY-MM-DD) */
  value: PropTypes.string,
  /** Callback fired when date changes; receives an ISO date string (YYYY-MM-DD) */
  onChange: PropTypes.func,
  /** Size variant controlling font size and padding */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Expands the input to fill its container width */
  fullWidth: PropTypes.bool,
  /** Disables the input */
  disabled: PropTypes.bool,
  /** Additional CSS classes applied to the wrapper element */
  className: PropTypes.string,
}

export default WingsDateInput
